<template>
	<Panel
		:title="panelTitle"
		:action="panelActions"
		class="panel-car-fax"
	>
		<div v-if="hasResponseError">
			<div>No CarFax Information Available.</div>
			<div v-if="response && response.description">
				{{ response.description }}
			</div>
		</div>

		<div v-else>
			<fieldset
				disabled
				:style="$grid('1-1-1')"
			>
				<InputTextbox
					label="Warranty Start Date"
					v-model:saturn="dealCarFaxInfo.warrantyStartDateTime"
					placeholder="Warranty Start Date"
					:showIcon="false"
					icon="fas fa-calendar"
				/>

				<InputTextbox
					label="Last Odometer Reading Date"
					v-model:saturn="dealCarFaxInfo.lastOdometerReadingDateTime"
					placeholder="Last Odometer Reading Date"
					:showIcon="false"
					icon="fas fa-calendar"
				/>

				<InputTextbox
					label="Last Odometer Reading"
					v-model:saturn="dealCarFaxInfo.lastOdometerReading"
					placeholder="Last Odometer Reading"
					:showIcon="false"
					icon="fas fa-hashtag"
				/>
			</fieldset>

			<table class="borderless-table">
				<thead>
					<tr>
						<td />
						<th>Manufacturer Months</th>
						<th>Manufacturer Miles</th>
						<th>Remaining Months</th>
						<th>Remaining Miles</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<th>Basic</th>
						<td>{{ dealCarFaxInfo.originalBasicMonths }}</td>
						<td>{{ formatNumber(dealCarFaxInfo.originalBasicMiles) }}</td>
						<td>{{ dealCarFaxInfo.remainingBasicMonths }}</td>
						<td>{{ formatNumber(dealCarFaxInfo.remainingBasicMiles) }}</td>
					</tr>
					<tr>
						<th>Drivetrain</th>
						<td>{{ dealCarFaxInfo.originalDrivetrainMonths }}</td>
						<td>{{ formatNumber(dealCarFaxInfo.originalDrivetrainMiles) }}</td>
						<td>{{ dealCarFaxInfo.remainingDrivetrainMonths }}</td>
						<td>{{ formatNumber(dealCarFaxInfo.remainingDrivetrainMiles) }}</td>
					</tr>
					<tr>
						<th>Roadside</th>
						<td>{{ dealCarFaxInfo.originalRoadsideMonths }}</td>
						<td>{{ formatNumber(dealCarFaxInfo.originalRoadsideMiles) }}</td>
						<td>{{ dealCarFaxInfo.remainingRoadsideMonths }}</td>
						<td>{{ formatNumber(dealCarFaxInfo.remainingRoadsideMiles) }}</td>
					</tr>
				</tbody>
			</table>

			<fieldset
				disabled
				class="extra-carfax-notes"
			>
				<InputTextbox
					label="Transferrable Note"
					v-model:saturn="dealCarFaxInfo.transferableNote"
					placeholder="Transferable Note"
					:showIcon="false"
					multiline
					:rows="hasTransferrableNote ? 2 : 1"
				/>

				<InputTextbox
					label="Warranty Notes"
					v-model:saturn="dealCarFaxInfo.warrantyNotes"
					placeholder="Warranty Notes"
					:showIcon="false"
					multiline
					:rows="hasWarrantyNotes ? 8 : 1"
				/>
			</fieldset>
		</div>
		<template #header>
			<TooltipComponent
				v-if="hasReportedAccident"
				:tooltip="[`Vehicle has Reported Accident/Damage - (${numberOfAccidents})`]"
				direction="right"
			>
				<template #showFromOutside>
					<i class="fas fa-car-crash bad" />
				</template>
			</TooltipComponent>
		</template>
	</Panel>
</template>
<script lang="ts">
	export interface ICarFaxResponse {
		status: boolean;
		description: string;
	}

	export interface IPanelCarFaxProps {
		panelTitle: string;
		dealCarFaxInfo?: FIMenuCarFax;
		vin?: string;
		response?: ICarFaxResponse;
		fimenu: FIMenu;
	}
</script>
<script setup lang="ts">
	import { computed, ref, inject, watch } from 'vue';
	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuCarFax from '@core/classes/FIMenuCarFax';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import modalInfo from '@core/modals/modalInfo.vue';
	import Panel from '@core/components/Panel.vue';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';
	import TooltipComponent from '@core/components/TooltipComponent.vue';

	const $global = inject<SaturnGlobalPlugin>('$global');

	const props = defineProps<IPanelCarFaxProps>();
	const dealCarFaxInfo = ref(props.dealCarFaxInfo);

	watch(
		() => props.dealCarFaxInfo,
		newCarFaxInfo => {
			dealCarFaxInfo.value = newCarFaxInfo;
		},
		{ deep: true },
	);

	//Updates the CarFax info on fimenu and updates inServiceDate on fimenu
	const emit = defineEmits(['updateDealCarFaxInfo', 'updateDealWarrantyInfo']);

	const hasResponseError = computed(() => {
		if (dealCarFaxInfo.value?.lastOdometerReading) return false;
		return !props.response?.status || (!props.response && !dealCarFaxInfo.value?.lastOdometerReading);
	});
	const panelActions = computed(() => {
		return [
			{
				text: 'View CarFax',
				icon: 'fas fa-external-link-alt',
				class: 'view-carfax-button ignore-all-locks',
				onClick: openCarFaxPage,
				disabled: !props.vin,
			},
			{
				text: 'Refresh CarFax',
				icon: 'fas fa-sync',
				class: 'view-carfax-button ignore-all-locks',
				onClick: refreshCarFax,
				disabled: (props.fimenu.isDealLocked() && !$global?.isAdminView) || !props.vin,
			},
		];
	});
	const hasTransferrableNote = computed(() => {
		return dealCarFaxInfo.value.transferableNote && !noCarFaxData(dealCarFaxInfo.value.transferableNote);
	});
	const hasWarrantyNotes = computed(() => {
		return dealCarFaxInfo.value.warrantyNotes && !noCarFaxData(dealCarFaxInfo.value.warrantyNotes);
	});
	const hasReportedAccident = computed(() => {
		return dealCarFaxInfo.value?.reportedAccident?.accidentReported;
	});
	const numberOfAccidents = computed(() => {
		return dealCarFaxInfo.value?.reportedAccident?.reportedAccidentInfo?.length;
	});

	async function openCarFaxPage() {
		if (props.vin) {
			const url = await api.carFax.getNewCarFaxURL(props.fimenu.storeCode, props.vin);
			dealCarFaxInfo.value.url = url.data;
			window.open(url.data, '_blank');
		}
	}
	async function refreshCarFax() {
		if (props.vin) {
			$modal.open(modalInfo, {
				name: 'modalInfo',
				passedData: {
					title: 'Warning: Updating CarFax Information',
					info: 'Refreshing CarFax data may update the warranty information and other details.\n\nAre you sure you want to continue?',
					acceptText: 'Yes',
					cancelText: 'No',
				},
				postFunction: async () => {
					const response = await api.carFax.DecodeCarFax(props.fimenu.storeCode, props.vin);
					if (response.data) {
						dealCarFaxInfo.value = new FIMenuCarFax(response.data.fiMenuCarFax);
						emit('updateDealCarFaxInfo', dealCarFaxInfo.value);
						emit('updateDealWarrantyInfo', dealCarFaxInfo.value);
					}
				},
			});
		}
	}
	function formatNumber(number: number) {
		if (number == null) return 'N/A';
		return number.toLocaleString();
	}
	function noCarFaxData(dataField: string) {
		return dataField === 'No data reported to CARFAX.';
	}
</script>
<style>
	.panel-car-fax .basic-car-info {
		display: flex;
		align-items: center;
		justify-content: space-evenly;
		gap: 10px;
	}

	.panel-car-fax .view-carfax-button {
		gap: 5px;
	}

	.panel-car-fax .borderless-table {
		border-top: 1px solid var(--border-color);
		border-bottom: 1px solid var(--border-color);
	}

	.panel-car-fax .extra-carfax-notes > * {
		margin: 10px 0;
	}

	.panel-car-fax .input-component-wrapper textarea:disabled,
	.panel-car-fax .input-component-wrapper input:disabled {
		background-color: var(--card-background-color);
		color: var(--text-color);
	}
</style>
